import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  VStack,
} from '@chakra-ui/react';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { MarketplaceContext } from 'src/contexts/marketplace';
import LinkInBioHeader from '../link-in-bio-header';
import * as Sentry from '@sentry/react';
import { useRouter } from 'next/router';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  fetchClientSecret: () => Promise<string>;
}

const CheckoutModal = ({ isOpen, onClose, fetchClientSecret }: CheckoutModalProps) => {
  const { marketplace, stripePromise } = useContext(MarketplaceContext);
  const [checkoutLoaded, setCheckoutLoaded] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [mountKey, setMountKey] = useState(0);
  const [shouldRenderCheckout, setShouldRenderCheckout] = useState(false);
  const router = useRouter();

  // Send an error to Sentry if the checkout hasn't loaded within 10s
  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      if (!checkoutLoaded) {
        Sentry.captureMessage('Stripe checkout took longer than 10 seconds to load.', {
          level: 'error',
          tags: {
            component: 'CheckoutModal',
            marketplaceId: marketplace?.id,
          },
        });
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, [isOpen, checkoutLoaded, marketplace?.id]);

  // Reset state and remount Stripe component when modal opens
  useEffect(() => {
    if (isOpen) {
      setAuthError(false);
      setCheckoutLoaded(false);
      setShouldRenderCheckout(false);
      // Increment mount key to force remount of EmbeddedCheckoutProvider
      setMountKey((prev) => prev + 1);

      // Delay rendering to ensure previous Stripe instance is fully cleaned up
      const timer = setTimeout(() => {
        setShouldRenderCheckout(true);
      }, 100);

      return () => clearTimeout(timer);
    } else {
      // When modal closes, immediately stop rendering checkout
      setShouldRenderCheckout(false);
    }
  }, [isOpen]);

  // Wrap fetchClientSecret so we can mark the checkout as loaded and handle errors
  const wrappedFetchClientSecret = useCallback(async () => {
    try {
      const clientSecret = await fetchClientSecret();

      // Check if client secret is valid
      if (!clientSecret || clientSecret.trim() === '') {
        const error = new Error('Invalid client secret received');
        Sentry.captureException(error, {
          tags: {
            component: 'CheckoutModal',
            errorType: 'invalid_client_secret',
            marketplaceId: marketplace?.id,
          },
        });
        throw error;
      }

      // Once we have the client secret, we assume the checkout can mount
      setCheckoutLoaded(true);
      return clientSecret;
    } catch (error: any) {
      // Check if this is an authentication error
      if (
        error?.isAuthError ||
        error?.message?.includes('Authentication failed') ||
        error?.message?.includes('not authenticated')
      ) {
        setAuthError(true);
        Sentry.captureException(error, {
          tags: {
            component: 'CheckoutModal',
            errorType: 'authentication',
            marketplaceId: marketplace?.id,
          },
          extra: {
            errorMessage: error?.message,
          },
        });
      } else {
        Sentry.captureException(error, {
          tags: {
            component: 'CheckoutModal',
            errorType: 'fetch_client_secret',
            marketplaceId: marketplace?.id,
          },
          extra: {
            errorMessage: error?.message,
          },
        });
      }

      throw error;
    }
  }, [fetchClientSecret, marketplace?.id]);

  // Memoize the onComplete callback
  const handleCheckoutComplete = useCallback(() => {
    Sentry.addBreadcrumb({
      category: 'checkout',
      message: 'Stripe checkout completed successfully',
      level: 'info',
    });
  }, []);

  // Memoize the options object to prevent prop changes
  const checkoutOptions = useMemo(
    () => ({
      fetchClientSecret: wrappedFetchClientSecret,
      onComplete: handleCheckoutComplete,
    }),
    [wrappedFetchClientSecret, handleCheckoutComplete],
  );

  // Error boundary wrapper for the Stripe checkout
  const handleError = (error: unknown) => {
    Sentry.captureException(error, {
      tags: {
        component: 'CheckoutModal',
        marketplaceId: marketplace?.id,
      },
      extra: {
        hasStripePromise: !!stripePromise,
        error,
      },
    });
  };

  useEffect(() => {
    if (isOpen && !stripePromise) {
      Sentry.captureMessage('Stripe Promise missing in CheckoutModal', {
        level: 'error',
        tags: {
          component: 'CheckoutModal',
          marketplaceId: marketplace?.id,
        },
      });
    }
  }, [isOpen, stripePromise, marketplace?.id]);

  const handleLoginRedirect = () => {
    const currentUrl = router.asPath;
    router.push(`/login?returnUrl=${encodeURIComponent(currentUrl)}`);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent maxWidth={{ base: '500px', lg: '1040px' }} maxHeight="85vh">
        <ModalHeader />
        <ModalCloseButton />
        <ModalBody overflowY="auto">
          <LinkInBioHeader marketplace={marketplace} />
          {authError ? (
            <VStack spacing={4} py={8}>
              <Alert
                status="error"
                variant="subtle"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                minHeight="200px"
                borderRadius="lg"
              >
                <AlertIcon boxSize="40px" mr={0} />
                <AlertTitle mt={4} mb={1} fontSize="lg">
                  Authentication Required
                </AlertTitle>
                <AlertDescription maxWidth="sm">
                  Your session has expired. Please log in again to complete your payment.
                </AlertDescription>
                <Button colorScheme="primary" mt={4} onClick={handleLoginRedirect}>
                  Go to Login
                </Button>
              </Alert>
            </VStack>
          ) : (
            <>
              {shouldRenderCheckout && stripePromise !== null && (
                <Sentry.ErrorBoundary
                  key={`error-boundary-${mountKey}`}
                  fallback={
                    <div>
                      We were unable to load checkout. Please refresh the page or contact support if
                      the issue persists.
                    </div>
                  }
                  onError={handleError}
                >
                  <EmbeddedCheckoutProvider
                    key={`checkout-provider-${mountKey}`}
                    stripe={stripePromise}
                    options={checkoutOptions}
                  >
                    <EmbeddedCheckout />
                  </EmbeddedCheckoutProvider>
                </Sentry.ErrorBoundary>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter />
      </ModalContent>
    </Modal>
  );
};

export default CheckoutModal;
